import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Play, X } from "lucide-react";
import { VIDEO } from "@/constants/testIds";

export default function ForecastVideos() {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
  }, []);

  return (
    <section id="forecast" className="relative py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#1f63c9]">Forecast Vault</div>
            <h2 className="font-oswald text-2xl font-bold uppercase tracking-wider text-white sm:text-3xl lg:text-4xl">
              Latest Forecast Videos
            </h2>
          </div>
          <div className="font-mono text-xs uppercase tracking-widest text-gray-400">
            {videos.length} clip{videos.length === 1 ? "" : "s"}
          </div>
        </div>

        {videos.length === 0 && (
          <div className="rounded border border-dashed border-white/15 p-10 text-center">
            <div className="font-oswald uppercase tracking-widest text-white">No forecast videos yet</div>
            <p className="mt-2 text-sm text-gray-400">
              Admins can add YouTube forecast videos from the admin dashboard.
            </p>
          </div>
        )}

        <div data-testid={VIDEO.grid} className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {videos.map((v) => (
            <button
              key={v.id}
              data-testid={VIDEO.card(v.id)}
              onClick={() => setActive(v)}
              className="lift group relative overflow-hidden rounded-sm border border-white/10 bg-[#151b26] text-left"
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src={v.thumbnail_url}
                  alt={v.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                <span
                  data-testid={VIDEO.playBtn(v.id)}
                  className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1f63c9] shadow-[0_8px_30px_rgba(217,4,41,0.6)]"
                >
                  <Play className="h-6 w-6 translate-x-0.5 text-white" fill="white" />
                </span>
              </div>
              <div className="p-4">
                <div className="line-clamp-2 font-oswald text-base font-semibold uppercase tracking-wider text-white">
                  {v.title}
                </div>
                {v.description && (
                  <div className="mt-1 line-clamp-2 text-sm text-gray-400">{v.description}</div>
                )}
                <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  {new Date(v.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {active && (
        <div
          data-testid={VIDEO.dialog}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setActive(null)}
        >
          <div
            className="relative w-full max-w-5xl overflow-hidden rounded border border-white/10 bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid={VIDEO.closeDialog}
              onClick={() => setActive(null)}
              className="absolute right-3 top-3 z-10 rounded-full bg-black/70 p-2 text-white hover:bg-[#1f63c9]"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="aspect-video w-full">
              <iframe
                src={`https://www.youtube.com/embed/${active.youtube_id}?autoplay=1`}
                title={active.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
            <div className="p-4">
              <div className="font-oswald text-lg font-semibold uppercase tracking-wider text-white">{active.title}</div>
              {active.description && <div className="mt-1 text-sm text-gray-400">{active.description}</div>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
